import React, { useCallback, useEffect, useState } from "react";
import { api, STATUSES, statusColor, PRIORITIES, priorityColor, priorityLabel, locationSuffix } from "../lib/api";
import { Link } from "react-router-dom";
import { Plus, ExternalLink, X, ArrowUpDown, ChevronLeft, ChevronRight, ArchiveRestore, Trash2, Archive } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useSettings } from "../context/SettingsContext";
import { useJobFilters, SORTS } from "../hooks/useJobFilters";
import { toast } from "sonner";

const PAGE_SIZE = 25;

export default function Jobs() {
  const { settings } = useSettings();
  const [jobs, setJobs] = useState([]);
  const {
    statusSel, setStatusSel, q, setQ, locQ, setLocQ, salMin, setSalMin, salMax, setSalMax,
    prioFilter, setPrioFilter, sortBy, setSortBy, toggleStatus, clearFilters, hasFilters, sorted,
  } = useJobFilters(jobs, settings.show_rejected_in_main);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [view, setView] = useState("active"); // "active" | "archived"
  const [archived, setArchived] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => { setSelected(new Set()); }, [view]); // reset selection when switching tabs

  const loadActive = useCallback(async () => {
    setLoading(true);
    try {
      const limit = 200;
      let p = 1, all = [], total = Infinity;
      while (all.length < total) {
        const r = await api.get(`/jobs?page=${p}&limit=${limit}`);
        all = all.concat(r.data);
        total = Number(r.headers["x-total-count"] ?? all.length);
        if (r.data.length < limit) break;
        p += 1;
      }
      setJobs(all);
    } finally { setLoading(false); }
  }, []);

  const loadArchived = useCallback(async () => {
    setArchivedLoading(true);
    try {
      const r = await api.get("/jobs", { params: { archived: true } });
      setArchived(r.data);
    } finally { setArchivedLoading(false); }
  }, []);

  useEffect(() => { loadActive(); }, [loadActive]);
  useEffect(() => {
    if (view === "archived") loadArchived();
  }, [view, loadArchived]);

  const unarchiveJob = async (job) => {
    await api.patch(`/jobs/${job.job_id}`, { archived: false });
    toast.success("Restored");
    loadArchived();
    loadActive();
  };

  const deleteArchivedJob = async (id) => {
    if (!window.confirm("Permanently delete this job?")) return;
    await api.delete(`/jobs/${id}`);
    toast.success("Deleted");
    loadArchived();
  };

  const toggleSelect = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const clearSelection = () => setSelected(new Set());
  const selectAll = (ids) =>
    setSelected((s) => {
      const allOn = ids.length > 0 && ids.every((id) => s.has(id));
      const n = new Set(s);
      ids.forEach((id) => (allOn ? n.delete(id) : n.add(id)));
      return n;
    });

  const runBulk = async (body, successMsg) => {
    const count = selected.size;
    try {
      await api.post("/jobs/bulk", { job_ids: [...selected], ...body });
      toast.success(successMsg(count));
      clearSelection();
      loadActive();
      if (view === "archived") loadArchived();
    } catch {
      toast.error("Bulk action failed");
    }
  };
  const bulkStatus = (status) => runBulk({ action: "update", status }, (n) => `Moved ${n} job${n > 1 ? "s" : ""} to ${status}`);
  const bulkPriority = (priority) => runBulk({ action: "update", priority }, (n) => `Updated priority for ${n} job${n > 1 ? "s" : ""}`);
  const bulkArchive = () => runBulk({ action: "archive" }, (n) => `Archived ${n} job${n > 1 ? "s" : ""}`);
  const bulkRestore = () => runBulk({ action: "unarchive" }, (n) => `Restored ${n} job${n > 1 ? "s" : ""}`);
  const bulkDelete = () => {
    if (!window.confirm(`Permanently delete ${selected.size} job(s)? This can't be undone.`)) return;
    runBulk({ action: "delete" }, (n) => `Deleted ${n} job${n > 1 ? "s" : ""}`);
  };

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  useEffect(() => { setPage(1); }, [q, locQ, salMin, salMax, statusSel, prioFilter, sortBy]);
  const currentPage = Math.min(page, totalPages);
  const pageItems = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Keep rejected out of the active-pipeline summary/filter chips unless the user opts in.
  const chipStatuses = settings.show_rejected_in_main ? STATUSES : STATUSES.filter((s) => s.key !== "rejected");
  const allChipCount = settings.show_rejected_in_main ? jobs.length : jobs.filter((j) => j.status !== "rejected").length;

  return (
    <div className="space-y-6" data-testid="jobs-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tiny">Pipeline</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">All jobs</h1>
        </div>
        <Link to="/jobs/new">
          <Button data-testid="jobs-add-btn" className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-10 px-4">
            <Plus size={16} className="mr-1" /> New job
          </Button>
        </Link>
      </div>

      {/* View tabs */}
      <div className="flex border border-zinc-200 w-fit" data-testid="jobs-view-tabs">
        <button
          data-testid="jobs-view-active"
          onClick={() => setView("active")}
          className={"px-4 py-2 text-xs uppercase tracking-wider font-semibold transition-colors " +
            (view === "active" ? "bg-zinc-900 text-white" : "hover:bg-zinc-100 text-zinc-700")}
        >
          Active
        </button>
        <button
          data-testid="jobs-view-archived"
          onClick={() => setView("archived")}
          className={"px-4 py-2 text-xs uppercase tracking-wider font-semibold transition-colors " +
            (view === "archived" ? "bg-zinc-900 text-white" : "hover:bg-zinc-100 text-zinc-700")}
        >
          Archived{archived.length > 0 ? ` · ${archived.length}` : ""}
        </button>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="sticky top-14 z-30 flex items-center gap-2 flex-wrap border-2 border-zinc-900 bg-white p-3 shadow-lg" data-testid="bulk-bar">
          <span className="font-mono-jb text-sm font-semibold" data-testid="bulk-count">{selected.size} selected</span>
          <div className="h-5 w-px bg-zinc-300 mx-1" />
          {view === "active" ? (
            <>
              <Select onValueChange={bulkStatus}>
                <SelectTrigger className="rounded-none h-9 w-40 border-zinc-300" data-testid="bulk-status-trigger"><SelectValue placeholder="Set status" /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.key} value={s.key} data-testid={`bulk-status-${s.key}`}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select onValueChange={bulkPriority}>
                <SelectTrigger className="rounded-none h-9 w-40 border-zinc-300" data-testid="bulk-priority-trigger"><SelectValue placeholder="Set priority" /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.key} value={p.key} data-testid={`bulk-priority-${p.key}`}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={bulkArchive} className="rounded-none h-9 border-zinc-300" data-testid="bulk-archive">
                <Archive size={14} className="mr-1.5" /> Archive
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={bulkRestore} className="rounded-none h-9 border-zinc-300" data-testid="bulk-restore">
                <ArchiveRestore size={14} className="mr-1.5" /> Restore
              </Button>
              <Button variant="ghost" onClick={bulkDelete} className="rounded-none h-9 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="bulk-delete">
                <Trash2 size={14} className="mr-1.5" /> Delete
              </Button>
            </>
          )}
          <button onClick={clearSelection} className="label-tiny ml-auto inline-flex items-center hover:text-zinc-900" data-testid="bulk-clear">
            <X size={12} className="mr-1" /> Clear
          </button>
        </div>
      )}

      {view === "active" && (
      <>
      {/* Filters */}
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1 border border-zinc-200">
          <FilterChip active={statusSel.length === 0} onClick={() => setStatusSel([])} tid="filter-all">
            All <span className="font-mono-jb ml-2 text-zinc-500">{allChipCount}</span>
          </FilterChip>
          {chipStatuses.map((s) => {
            const c = jobs.filter((j) => j.status === s.key).length;
            return (
              <FilterChip key={s.key} active={statusSel.includes(s.key)} onClick={() => toggleStatus(s.key)} tid={`filter-${s.key}`}>
                <span className="status-dot" style={{ backgroundColor: s.color }} />
                {s.label} <span className="font-mono-jb ml-2 text-zinc-500">{c}</span>
              </FilterChip>
            );
          })}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <Input data-testid="jobs-search" placeholder="Search title / company…" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-none border-zinc-200 h-10" />
          <Input data-testid="jobs-filter-location" placeholder="Location…" value={locQ} onChange={(e) => setLocQ(e.target.value)} className="rounded-none border-zinc-200 h-10" />
          <Input data-testid="jobs-filter-salary-min" type="number" placeholder="Min salary" value={salMin} onChange={(e) => setSalMin(e.target.value)} className="rounded-none border-zinc-200 h-10" />
          <Input data-testid="jobs-filter-salary-max" type="number" placeholder="Max salary" value={salMax} onChange={(e) => setSalMax(e.target.value)} className="rounded-none border-zinc-200 h-10" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ArrowUpDown size={14} className="text-zinc-500" />
          <span className="label-tiny">Sort by</span>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="rounded-none h-9 w-44 border-zinc-200" data-testid="jobs-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORTS.map((s) => (
                <SelectItem key={s.key} value={s.key} data-testid={`sort-${s.key}`}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="label-tiny ml-2">Priority</span>
          <Select value={prioFilter} onValueChange={setPrioFilter}>
            <SelectTrigger className="rounded-none h-9 w-40 border-zinc-200" data-testid="jobs-priority-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="prio-filter-all">All priorities</SelectItem>
              {PRIORITIES.map((p) => (
                <SelectItem key={p.key} value={p.key} data-testid={`prio-filter-${p.key}`}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {hasFilters && (
          <button onClick={clearFilters} data-testid="jobs-clear-filters" className="label-tiny inline-flex items-center hover:text-zinc-900">
            <X size={12} className="mr-1" /> Clear filters · {sorted.length} shown
          </button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-2">{["a","b","c","d","e"].map((k) => <div key={k} className="h-20 bg-zinc-100" />)}</div>
      ) : sorted.length === 0 ? (
        <div className="brutal-card p-10 text-center">
          <div className="label-tiny">No jobs found</div>
          <p className="mt-3 text-zinc-600">Try a different filter or add a new job.</p>
        </div>
      ) : (
        <div className="border border-zinc-200" data-testid="jobs-list">
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-200 bg-zinc-50">
            <Checkbox
              checked={pageItems.length > 0 && pageItems.every((j) => selected.has(j.job_id))}
              onCheckedChange={() => selectAll(pageItems.map((j) => j.job_id))}
              data-testid="bulk-select-all"
              aria-label="Select all on page"
            />
            <span className="label-tiny">Select page</span>
          </div>
          {pageItems.map((j) => (
            <div
              key={j.job_id}
              className={"flex items-center justify-between gap-3 p-4 border-b border-zinc-200 last:border-b-0 transition-colors " + (selected.has(j.job_id) ? "bg-zinc-100" : "hover:bg-zinc-50")}
              data-testid={`job-row-${j.job_id}`}
            >
              <Checkbox
                checked={selected.has(j.job_id)}
                onCheckedChange={() => toggleSelect(j.job_id)}
                data-testid={`select-${j.job_id}`}
                aria-label={`Select ${j.title}`}
              />
              <Link to={`/jobs/${j.job_id}`} className="min-w-0 flex-1">
                <div className="font-medium truncate">{j.title}</div>
                <div className="text-sm text-zinc-500 truncate">
                  {j.company}{locationSuffix(j)}{j.salary_display ? ` · ${j.salary_display}` : ""}
                </div>
              </Link>
              <div className="flex items-center gap-3 shrink-0">
                <span className="hidden sm:flex items-center text-xs text-zinc-500" data-testid={`job-priority-${j.job_id}`} title={`${priorityLabel(j.priority)} priority`}>
                  <span className="status-dot" style={{ backgroundColor: priorityColor(j.priority) }} />
                  <span className="font-mono-jb uppercase tracking-wider">{priorityLabel(j.priority)}</span>
                </span>
                <span className="flex items-center text-xs">
                  <span className="status-dot" style={{ backgroundColor: statusColor(j.status) }} />
                  <span className="font-mono-jb uppercase tracking-wider hidden sm:inline">{j.status}</span>
                </span>
                {j.url && (
                  <a href={j.url} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-zinc-900" data-testid={`apply-link-${j.job_id}`}>
                    <ExternalLink size={16} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && sorted.length > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-4 flex-wrap" data-testid="jobs-pager">
          <span className="label-tiny" data-testid="jobs-pager-info">
            {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, sorted.length)} of {sorted.length}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              data-testid="jobs-pager-prev"
              className="rounded-none border-zinc-300 h-9 px-3"
            >
              <ChevronLeft size={16} /> Prev
            </Button>
            <span className="label-tiny px-3" data-testid="jobs-pager-page">Page {currentPage} / {totalPages}</span>
            <Button
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              data-testid="jobs-pager-next"
              className="rounded-none border-zinc-300 h-9 px-3"
            >
              Next <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}
      </>
      )}

      {view === "archived" && (
        archivedLoading ? (
          <div className="space-y-2">{["a","b","c"].map((k) => <div key={k} className="h-20 bg-zinc-100" />)}</div>
        ) : archived.length === 0 ? (
          <div className="brutal-card p-10 text-center" data-testid="archive-empty">
            <div className="label-tiny">Archive is empty</div>
            <p className="mt-3 text-zinc-600">Rejected jobs and ones you archive manually will land here.</p>
          </div>
        ) : (
          <div className="border border-zinc-200" data-testid="archived-list">
            <div className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-200 bg-zinc-50">
              <Checkbox
                checked={archived.length > 0 && archived.every((j) => selected.has(j.job_id))}
                onCheckedChange={() => selectAll(archived.map((j) => j.job_id))}
                data-testid="bulk-select-all-archived"
                aria-label="Select all archived"
              />
              <span className="label-tiny">Select all</span>
            </div>
            {archived.map((j) => (
              <div key={j.job_id} className={"flex items-center justify-between gap-3 p-4 border-b border-zinc-200 last:border-b-0 " + (selected.has(j.job_id) ? "bg-zinc-100" : "hover:bg-zinc-50")} data-testid={`archived-row-${j.job_id}`}>
                <Checkbox
                  checked={selected.has(j.job_id)}
                  onCheckedChange={() => toggleSelect(j.job_id)}
                  data-testid={`select-${j.job_id}`}
                  aria-label={`Select ${j.title}`}
                />
                <Link to={`/jobs/${j.job_id}`} className="min-w-0 flex-1">
                  <div className="font-medium truncate">{j.title}</div>
                  <div className="text-sm text-zinc-500 truncate">{j.company}{locationSuffix(j)}</div>
                </Link>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="hidden sm:flex items-center text-xs">
                    <span className="status-dot" style={{ backgroundColor: statusColor(j.status) }} />
                    <span className="font-mono-jb uppercase tracking-wider">{j.status}</span>
                  </span>
                  <Button variant="outline" onClick={() => unarchiveJob(j)} className="rounded-none border-zinc-300 h-9" data-testid={`unarchive-${j.job_id}`}>
                    <ArchiveRestore size={14} className="mr-1.5" /> Restore
                  </Button>
                  <Button variant="ghost" onClick={() => deleteArchivedJob(j.job_id)} className="rounded-none text-red-600 hover:text-red-700 hover:bg-red-50 h-9" data-testid={`delete-${j.job_id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Mobile FAB */}
      <Link to="/jobs/new" className="md:hidden fixed bottom-20 right-4 z-40">
        <button data-testid="fab-add-job" className="w-14 h-14 bg-zinc-900 text-white flex items-center justify-center shadow-lg">
          <Plus size={24} />
        </button>
      </Link>
    </div>
  );
}

const FilterChip = ({ active, onClick, children, tid }) => (
  <button
    data-testid={tid}
    onClick={onClick}
    className={"px-3 py-2 text-xs uppercase tracking-wider font-semibold flex items-center transition-colors " +
      (active ? "bg-zinc-900 text-white" : "hover:bg-zinc-100 text-zinc-700")}
  >
    {children}
  </button>
);

